import Text from '@atoms/Text/Text';
import { IStickySubNavigation } from '@models/IStickySubNavigation';
import SiteEnum from '@models/enums/SiteEnum';
import throttle from '@utility/helpers/throttle';
import useMediaQuery from '@utility/hooks/useMediaQuery';
import classNames from 'classnames';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useCallback, useEffect, useRef, useState } from 'react';

interface Props extends IStickySubNavigation {
  readonly showNavigation?: boolean;
}

const StickySubNavigation = ({
  site,
  title,
  items,
  showNavigation = true,
}: Props) => {
  const [activeElement, setActiveElement] = useState<string>('');
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [pageTop, setPageTop] = useState(true);
  const [scrolledUp, setScrolledUp] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const isMobile = useMediaQuery(752);
  const ref = useRef<HTMLElement>(null);
  const [headerElement, setHeaderElement] = useState<HTMLElement | null>(null);
  const shouldReduceMotion = useReducedMotion();

  const sectionIds = useCallback(
    () => items.map((item) => `#a-${item.id}`),
    [items]
  );

  // Watch for element ids on the page to update active section in the navigation
  const updateActiveSection = useCallback(() => {
    const elements = document.querySelectorAll(sectionIds().toString());

    const observer = new IntersectionObserver(
      (entries) => {
        const listOfCurrentlyIntersecting = entries.filter(
          (entry) => entry.isIntersecting
        );

        let chosenItem: IntersectionObserverEntry | undefined;

        if (listOfCurrentlyIntersecting.length === 1) {
          chosenItem = listOfCurrentlyIntersecting.at(0);
        }

        if (listOfCurrentlyIntersecting.length > 1) {
          let found: IntersectionObserverEntry | undefined;
          listOfCurrentlyIntersecting.forEach((el) => {
            if (!found) found = el;

            if (el.intersectionRatio >= found?.intersectionRatio) found = el;
          });

          chosenItem = found;
        }

        if (chosenItem && chosenItem.target.id.substring(2) !== activeElement) {
          setActiveElement(chosenItem?.target.id.substring(2) || '');
        }
      },
      {
        rootMargin: '-30% 0px -30% 0px',
        threshold: [0, 1],
      }
    );

    elements.forEach((el) => observer.observe(el));
  }, [sectionIds, activeElement]);

  useEffect(() => {
    window.addEventListener('scroll', updateActiveSection);

    return () => window.removeEventListener('scroll', updateActiveSection);
  }, [updateActiveSection]);

  // Hide header if the user scrolls 200px and show if user scrolls back up
  useEffect(() => {
    let prevScrollY = window.scrollY;

    const handleScroll = throttle(() => {
      const currentScrollY = window.scrollY;
      const isScrollLocked = !!document.body.style.top;

      setPageTop(currentScrollY <= 0);

      setHasScrolled(currentScrollY >= 200 || isScrollLocked);

      if (prevScrollY > 0)
        setScrolledUp(currentScrollY < prevScrollY && !isScrollLocked);

      prevScrollY = currentScrollY;
    }, 100);

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    setHeaderElement(document.getElementById('header-wrapper'));

    if (headerElement) {
      headerElement.style.transform = isHeaderVisible
        ? 'translateY(0%)'
        : 'translateY(-100%)';
    }
  }, [isHeaderVisible]);

  useEffect(() => {
    setIsHeaderVisible(!hasScrolled || scrolledUp);
  }, [scrolledUp, hasScrolled]);

  // Scroll navigation horizontally to active item
  useEffect(() => {
    const navItem = document.getElementById(`nav-${activeElement}`);

    if (navItem) {
      navItem?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }, [activeElement]);

  if (!showNavigation) return;

  const slideDown = {
    animate: {
      y: 0,
    },
    exit: {
      y: isMobile ? 0 : '-100%',
    },
  };

  // Scroll to element when navigation clicked
  const scrollToId = (id: string) => {
    document
      .querySelectorAll('img[loading="lazy"]')
      .forEach((img) => img.setAttribute('loading', 'eager'));

    setTimeout(() => {
      const element = document.getElementById(id);
      element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 500);
  };

  return (
    <AnimatePresence>
      {hasScrolled && (
        <motion.nav
          key="sticky-nav"
          ref={ref}
          variants={slideDown}
          initial="exit"
          animate="animate"
          exit="exit"
          transition={{
            duration: shouldReduceMotion ? 0 : 0.3,
            ease: 'easeInOut',
          }}
          className={classNames(
            pageTop && isMobile && !hasScrolled ? 'tw-sticky' : '',
            'tw-fixed tw-left-0 tw-top-0 tw-z-40 tw-w-full md:tw-fixed'
          )}
        >
          <motion.div
            className={classNames(
              'tw-border-b tw-border-grey-20 tw-bg-white tw-text-grey-100 tw-transition-transform tw-duration-300 tw-ease-in-out'
            )}
            style={{
              translateY: isHeaderVisible
                ? document.getElementById('header')?.scrollHeight
                : 0,
            }}
          >
            <div
              className={
                'tw-flex tw-w-full tw-flex-col tw-items-center tw-justify-between md:tw-container md:tw-h-[60px] md:tw-flex-row md:tw-gap-sm'
              }
            >
              {/* Mobile Top Bar */}
              <AnimatePresence mode="wait">
                {isMobile && (
                  <motion.div
                    key="navTopBar"
                    variants={slideDown}
                    initial="exit"
                    animate="animate"
                    exit="exit"
                    transition={{
                      duration: shouldReduceMotion ? 0 : 0.3,
                      ease: 'easeInOut',
                    }}
                    className="tw-flex tw-w-full tw-items-center tw-justify-between tw-whitespace-nowrap tw-p-xs"
                  >
                    <Text variant="heading-24-light" className="tw-font-light">
                      {title}
                    </Text>
                  </motion.div>
                )}
              </AnimatePresence>
              <div className="tw-hidden tw-w-auto tw-justify-between tw-whitespace-nowrap tw-p-0 md:tw-flex">
                <Text variant="heading-24-light" className="tw-font-light">
                  {title}
                </Text>
              </div>
              <div className="tw-flex tw-h-full tw-w-full tw-overflow-hidden">
                <div className="tw-no-scrollbar tw-relative tw-h-[56px] tw-select-none tw-snap-x tw-snap-proximity tw-overflow-x-auto tw-whitespace-nowrap md:tw-h-auto">
                  <ul className="tw-flex tw-h-full tw-gap-sm tw-pb-sm md:tw-pb-0">
                    {items.map(({ title, id }) => (
                      <li
                        key={id}
                        id={`nav-${id}`}
                        className={classNames(
                          'tw-will-change-font tw-flex tw-h-md tw-snap-center tw-items-center tw-text-grey-70 tw-transition-all tw-duration-100 first:tw-ml-xs last:tw-mr-xs hover:tw-text-grey-100 md:tw-h-full rtl:first:tw-mr-xs rtl:last:tw-ml-xs',
                          {
                            'tw-border-b-2 ': id === activeElement,
                          },
                          site === SiteEnum.B2B
                            ? 'tw-border-yellow'
                            : 'tw-border-grey-80'
                        )}
                      >
                        <button
                          type="button"
                          onClick={() => scrollToId(`a-${id}`)}
                          className="tw-relative"
                        >
                          <Text
                            variant="body-small"
                            className={classNames(
                              'tw-absolute tw-inset-0 tw-font-semibold tw-text-grey-100',
                              id === activeElement
                                ? 'tw-opacity-100'
                                : 'tw-opacity-0'
                            )}
                          >
                            {title}
                          </Text>

                          <Text
                            variant="body-small"
                            className={classNames({
                              'tw-opacity-0': id === activeElement,
                            })}
                          >
                            {title}
                          </Text>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.nav>
      )}
    </AnimatePresence>
  );
};
export default StickySubNavigation;
